import { useEffect, useState } from 'react';
import {
  LayoutDashboard,
  Building2,
  Users,
  Handshake,
  Lock,
  FolderTree,
  Briefcase,
  Target,
  LogOut,
  FileSpreadsheet,
  CheckSquare,
  ShieldCheck,
  FolderKanban,
  ChevronRight,
  FolderOpen,
  BarChart3,
  Network,
  CalendarDays,
  GitBranch,
  GraduationCap,
  Bot,
  BellRing,
  ClipboardList,
  Sparkles,
} from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { keyResultService } from '@/services/api';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarFooter,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import koraIcon from '@/assets/imgs/kora-icon.svg';

interface MenuItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  module?: string; // Módulo ao qual este item pertence
  // 'always': sempre com cadeado. 'withoutModule': com cadeado só quando a empresa não tem o módulo
  // (em vez de sumir do menu).
  locked?: 'always' | 'withoutModule';
  // true: basta o módulo estar habilitado em alguma empresa da pessoa (não só na ativa).
  anyEmpresa?: boolean;
}

// A visibilidade de cada subitem é decidida ao montar a seção (getSections), não aqui.
interface SubMenuItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface MenuSection {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  items: SubMenuItem[];
}

export function AppSidebar() {
  const { user, logout } = useAuth();
  const { hasPermission, hasPermissionInAnyEmpresa } = usePermissions();
  const { state, isMobile, setOpenMobile, toggleSidebar } = useSidebar();
  const isCollapsed = !isMobile && state === 'collapsed';
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const [isLoadingApprovals, setIsLoadingApprovals] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  const toggleSection = (key: string) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Filtrar itens do menu principal por módulos.
  // Ordem definida em 2026-10-23: Dashboard, seção Cadastros (ex-"Gestão"), Projetos, OKRs, Organograma,
  // Fluxogramas, Calendário, Formulários, Treinamentos, CRM, IA; depois Aprovações (admin) e a seção Análise.
  const dashboardItem: MenuItem = { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard }; // Sempre visível
  const mainItems: MenuItem[] = [
    // Projetos (ex-ProjetoT) substitui desde 2026-09-23 os antigos Projetos e Cronogramas.
    { title: 'Projetos', url: '/projetos', icon: FolderKanban, module: 'projetos' },
    // OKRs: todos (inclusive superadmin) acessam a tela operacional (criar/editar/excluir).
    { title: 'OKRs', url: '/okrs', icon: Target, module: 'okrs' },
    { title: 'Organograma', url: '/organograma', icon: Network, module: 'organograma' },
    { title: 'Fluxogramas', url: '/fluxogramas', icon: GitBranch, module: 'fluxogramas' },
    // Agenda única por pessoa: aparece se QUALQUER empresa da pessoa tiver o módulo.
    { title: 'Calendário', url: '/calendario', icon: CalendarDays, module: 'calendario', anyEmpresa: true },
    { title: 'Formulários', url: '/formularios', icon: ClipboardList, module: 'gestao-atividades' },
    { title: 'Treinamentos', url: '/treinamentos', icon: GraduationCap, module: 'treinamentos' },
    { title: 'CRM', url: '/crm', icon: Handshake, module: 'crm', locked: 'withoutModule' },
    { title: 'IA', url: '/ia', icon: Bot, locked: 'always' },
  ];

  // Filtrar por módulos habilitados
  const filteredMainItems = mainItems.filter(item =>
    !item.module || item.locked === 'withoutModule' || (item.anyEmpresa ? hasPermissionInAnyEmpresa(item.module) : hasPermission(item.module))
  );

  const isItemLocked = (item: MenuItem) =>
    item.locked === 'always' || (item.locked === 'withoutModule' && !!item.module && !hasPermission(item.module));

  const lockReason = (item: MenuItem) =>
    item.locked === 'withoutModule' ? 'Módulo não contratado — fale com o administrador' : 'Recurso bloqueado';

  // Seções expansíveis (null quando a seção não tem item visível)
  const getSections = (): { cadastros: MenuSection | null; analise: MenuSection | null } => {
    // Seção Cadastros (chamava "Gestão" até 2026-10-23)
    const cadastrosItems: SubMenuItem[] = [];
    if (hasPermission('okrs') || hasPermission('projetos')) {
      // "Minha Empresa": pra admin é sempre a própria empresa; pra superadmin reflete a
      // empresa selecionada no filtro do cabeçalho (empresaFiltroId) — ver MinhaEmpresa.tsx.
      if (user?.tipo === 'admin' || user?.tipo === 'superadmin') {
        cadastrosItems.push({ title: 'Minha Empresa', url: '/minha-empresa', icon: Building2 });
      }
      // "Empresas" (lista todas) fica só como botão no cabeçalho pra superadmin (ver
      // AppLayout.tsx) — não duplicar aqui no menu "Cadastros".
    }
    if (hasPermission('okrs')) {
      cadastrosItems.push({ title: 'Departamentos', url: '/departamentos', icon: FolderTree });
      cadastrosItems.push({ title: 'Cargos', url: '/cargos', icon: Briefcase });
    }
    if (user?.tipo === 'admin' || user?.tipo === 'superadmin') {
      // Usuários vale para admin de qualquer empresa (antes sumia sem o módulo OKRs).
      cadastrosItems.push({ title: 'Usuários', url: '/usuarios', icon: Users });
      // Equipe inteira (pessoas, departamentos e cargos) numa planilha só (docs/cadastro-inteligente-2026-11-01.md).
      // Primeiro item da seção (pedido de 01/11/2026).
      cadastrosItems.unshift({ title: 'Cadastro inteligente', url: '/cadastro-inteligente', icon: Sparkles });
    }

    // Análise section
    const analiseItems: SubMenuItem[] = [];
    if (hasPermission('relatorios')) {
      analiseItems.push({ title: 'Relatórios', url: '/relatorios', icon: FileSpreadsheet });
    }
    if (hasPermission('auditoria')) {
      analiseItems.push({ title: 'Auditoria', url: '/auditoria', icon: ShieldCheck });
    }
    if ((user?.tipo === 'admin' || user?.tipo === 'superadmin') && hasPermission('okrs')) {
      analiseItems.push({ title: 'Notificações', url: '/notificacoes-envios', icon: BellRing });
    }

    return {
      cadastros: cadastrosItems.length > 0 ? { title: 'Cadastros', icon: FolderOpen, items: cadastrosItems } : null,
      analise: analiseItems.length > 0 ? { title: 'Análise', icon: BarChart3, items: analiseItems } : null,
    };
  };

  // Cadastros fica logo abaixo do Dashboard; Análise vai no fim do menu.
  const { cadastros, analise } = getSections();

  const renderMainItem = (item: MenuItem) => (
    <SidebarMenuItem key={item.title}>
      {isItemLocked(item) ? (
        <SidebarMenuButton
          tooltip={`${item.title} — ${lockReason(item)}`}
          title={lockReason(item)}
          aria-disabled="true"
          onClick={(e) => e.preventDefault()}
          // `aria-disabled:pointer-events-auto` mantém o tooltip/title funcionando (o padrão do botão desliga o mouse).
          className="cursor-not-allowed justify-between gap-3 rounded-lg px-3 py-2 text-muted-foreground opacity-60 aria-disabled:pointer-events-auto aria-disabled:opacity-60 hover:bg-transparent hover:text-muted-foreground"
        >
          <div className="relative flex items-center gap-3">
            <item.icon className="h-4 w-4" />
            {/* Sidebar recolhida: o cadeado do lado direito some, então vai sobre o ícone. */}
            <Lock className="absolute -bottom-1 -left-1 hidden h-2.5 w-2.5 group-data-[collapsible=icon]:block" aria-hidden="true" />
            <span>{item.title}</span>
            <span className="sr-only">({lockReason(item)})</span>
          </div>
          <Lock className="h-3.5 w-3.5 group-data-[collapsible=icon]:hidden" aria-hidden="true" />
        </SidebarMenuButton>
      ) : (
        <SidebarMenuButton asChild tooltip={item.title}>
          <NavLink
            to={item.url}
            onClick={() => setOpenMobile(false)}
            className="flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
          >
            <div className="flex items-center gap-3">
              <item.icon className="h-4 w-4" />
              <span>{item.title}</span>
            </div>
          </NavLink>
        </SidebarMenuButton>
      )}
    </SidebarMenuItem>
  );

  const renderSection = (section: MenuSection) => (
    <CollapsibleSection
      key={section.title}
      section={section}
      isOpen={openSections[section.title] ?? false}
      onToggle={() => toggleSection(section.title)}
      setOpenMobile={setOpenMobile}
    />
  );

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      <SidebarHeader className="px-2 py-3 md:px-3 md:py-4" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
        <div className="w-full">
          {isCollapsed ? (
            <div className="hidden flex-col items-center justify-center gap-2 md:flex">
              <button type="button" onClick={toggleSidebar} title="Expandir menu">
                <img src={koraIcon} alt="kora" className="h-8 w-8 object-contain" />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-1">
              <div className="hidden w-full items-center justify-end md:flex">
                <SidebarTrigger />
              </div>
              <img src={koraIcon} alt="kora" className="h-12 w-12 object-contain" />
              <div className="text-center leading-tight">
                <span className="font-display block text-sm font-bold text-sidebar-foreground">kora</span>
                <span className="block text-[9px] tracking-wide text-sidebar-foreground/60">gestão estratégica</span>
              </div>
            </div>
          )}
        </div>
      </SidebarHeader>
      <div className="mx-3 border-b border-sidebar-border/60" />
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {/* Itens principais filtrados por módulos, com Cadastros logo abaixo do Dashboard */}
              {renderMainItem(dashboardItem)}
              {cadastros && renderSection(cadastros)}
              {filteredMainItems.map(renderMainItem)}

              {/* Aprovações (apenas admin) - ligado ao módulo OKRs */}
              {user?.tipo === 'admin' && hasPermission('okrs') && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Aprovações">
                    <NavLink
                      to="/aprovacoes"
                      onClick={() => setOpenMobile(false)}
                      className="flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                      activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                    >
                      <div className="flex items-center gap-3">
                        <CheckSquare className="h-4 w-4" />
                        <span>Aprovações</span>
                      </div>
                      {pendingApprovalsCount > 0 && (
                        <Badge variant="destructive" className="ml-auto text-xs">
                          {pendingApprovalsCount}
                        </Badge>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {analise && renderSection(analise)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="p-4">
        <Button
          variant="ghost"
          onClick={logout}
          title={isCollapsed ? 'Sair' : undefined}
          className={cn(
            'text-primary hover:text-primary hover:bg-primary/20',
            isCollapsed ? 'mx-auto w-8 justify-center px-0' : 'w-full justify-start gap-3',
          )}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!isCollapsed && <span>Sair</span>}
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
}

// Componente para seções expansíveis
function CollapsibleSection({
  section,
  isOpen,
  onToggle,
  setOpenMobile,
}: {
  section: MenuSection;
  isOpen: boolean;
  onToggle: () => void;
  setOpenMobile: (open: boolean) => void;
}) {
  const SectionIcon = section.icon;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        onClick={onToggle}
        tooltip={section.title}
        className={cn(
          "flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
          isOpen && "bg-sidebar-accent text-sidebar-accent-foreground"
        )}
      >
        <div className="flex items-center gap-3">
          <SectionIcon className="h-4 w-4" />
          <span>{section.title}</span>
        </div>
        <ChevronRight
          className={cn(
            "h-3 w-3 shrink-0 transition-transform duration-200",
            isOpen && "rotate-90"
          )}
        />
      </SidebarMenuButton>
      {isOpen && (
        <SidebarMenuSub>
          {section.items.map((item) => (
            <SidebarMenuSubItem key={item.title}>
              <SidebarMenuSubButton asChild>
                <NavLink
                  to={item.url}
                  onClick={() => setOpenMobile(false)}
                  className="flex items-center gap-3 rounded-md px-2 text-muted-foreground transition-colors hover:text-sidebar-accent-foreground"
                  activeClassName="text-sidebar-accent-foreground font-medium"
                >
                  <item.icon className="h-3.5 w-3.5" />
                  <span>{item.title}</span>
                </NavLink>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          ))}
        </SidebarMenuSub>
      )}
    </SidebarMenuItem>
  );
}
